import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { getTeachersBySchoolId, updateTeacherProfileWithEmail } from "../services/TeacherAPIClient";
import "./TeacherUpdateUserEmailModal.css";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normaliseEmail = (value) => String(value || "").trim().toLowerCase();

const normalisePrefill = (prefill) => ({
  teacherName: String(prefill?.teacherName || prefill?.teacher_name || "").trim(),
  currentEmail: String(prefill?.currentEmail || prefill?.current_email || "").trim(),
  newEmail: String(
    prefill?.newEmail ||
    prefill?.new_email ||
    prefill?.email ||
    ""
  ).trim()
});

const normaliseTeacher = (teacher) => ({
  ...teacher,
  id: teacher?.id || teacher?._id || teacher?.teacherid || teacher?.teacherId || teacher?.teacher_id,
  name: teacher?.name || teacher?.fullName || teacher?.displayName || "Unnamed teacher",
  email: teacher?.email || teacher?.currentEmail || "",
  role: teacher?.role || teacher?.roleLabel || teacher?.position || ""
});

const findSingleExactMatch = (items, query, getValue) => {
  const target = normaliseText(query);

  if (!target) {
    return null;
  }

  const matches = items.filter((item) => normaliseText(getValue(item)) === target);
  return matches.length === 1 ? matches[0] : null;
};

function TeacherUpdateUserEmailModal({
  show,
  handleClose,
  schoolid,
  teacherid = null,
  currentTeacherId = null,
  currentTeacherName = "",
  currentTeacherEmail = "",
  teacherEmail = "",
  prefill = null,
  onSendPasswordReset = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [path, setPath] = useState("colleague");
  const [teachers, setTeachers] = useState([]);
  const [teacherSearchTerm, setTeacherSearchTerm] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selfTeacher, setSelfTeacher] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [requestedChange, setRequestedChange] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);
  const activeTeacherId = currentTeacherId || teacherid;

  const filteredTeachers = useMemo(() => {
    const query = normaliseText(teacherSearchTerm);

    if (!query) {
      return [];
    }

    return teachers.filter((teacher) =>
      [teacher.name, teacher.email, teacher.role].some((value) =>
        normaliseText(value).includes(query)
      )
    );
  }, [teacherSearchTerm, teachers]);

  const targetTeacher = path === "self" ? selfTeacher : selectedTeacher;
  const currentEmail = targetTeacher?.email || prefillContext.currentEmail || "";
  const trimmedNewEmail = newEmail.trim();
  const emailIsValid = EMAIL_PATTERN.test(trimmedNewEmail);
  const emailIsChanged =
    Boolean(currentEmail) && normaliseEmail(currentEmail) !== normaliseEmail(trimmedNewEmail);
  const canContinue = Boolean(targetTeacher && emailIsValid && emailIsChanged && !isSubmitting);

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("form");
    setPath(prefillContext.teacherName ? "colleague" : "self");
    setTeachers([]);
    setTeacherSearchTerm(prefillContext.teacherName);
    setSelectedTeacher(null);
    setSelfTeacher({
      id: activeTeacherId || "current-teacher",
      name: currentTeacherName || "My email",
      email: currentTeacherEmail || teacherEmail || prefillContext.currentEmail,
      role: "Signed-in teacher"
    });
    setNewEmail(prefillContext.newEmail);
    setRequestedChange(null);
    setError("");

    if (!schoolid) {
      return;
    }

    setIsLoading(true);

    getTeachersBySchoolId(schoolid)
      .then((teachersResponse) => {
        const allTeachers = (Array.isArray(teachersResponse) ? teachersResponse : [])
          .map(normaliseTeacher)
          .filter((teacher) => teacher.id);
        const normalisedTeachers = allTeachers
          .filter((teacher) =>
            teacher.active !== false ||
            normaliseText(teacher.name) === normaliseText(prefillContext.teacherName) ||
            normaliseEmail(teacher.email) === normaliseEmail(prefillContext.currentEmail)
          )
          .sort((firstTeacher, secondTeacher) => firstTeacher.name.localeCompare(secondTeacher.name));
        const currentTeacher =
          normalisedTeachers.find((teacher) => String(teacher.id) === String(activeTeacherId)) ||
          normalisedTeachers.find(
            (teacher) =>
              currentTeacherEmail &&
              normaliseEmail(teacher.email) === normaliseEmail(currentTeacherEmail)
          );
        const prefilledTeacher = findSingleExactMatch(
          normalisedTeachers,
          prefillContext.teacherName,
          (teacher) => teacher.name
        );

        setTeachers(normalisedTeachers);

        if (currentTeacher) {
          setSelfTeacher({ ...currentTeacher, role: currentTeacher.role || "Signed-in teacher" });
        }

        if (prefilledTeacher) {
          setSelectedTeacher(prefilledTeacher);
          setPath(
            activeTeacherId && prefilledTeacher.id === activeTeacherId
              ? "self"
              : "colleague"
          );
        }
      })
      .catch((loadError) => {
        console.error("Error loading update email workflow:", loadError);
        setError("Teachers could not be loaded. You can still request a change for your own email.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    show,
    schoolid,
    activeTeacherId,
    currentTeacherName,
    currentTeacherEmail,
    teacherEmail,
    prefillContext
  ]);

  const handlePathChange = (nextPath) => {
    setPath(nextPath);
    setSelectedTeacher(null);
    setTeacherSearchTerm(nextPath === "colleague" ? prefillContext.teacherName : "");
    setError("");
  };

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    setError("");
  };

  const handleContinue = () => {
    if (!targetTeacher) {
      setError(path === "self" ? "Your email details are not available." : "Choose the colleague first.");
      return;
    }

    if (!emailIsValid) {
      setError("Enter a valid new email address.");
      return;
    }

    if (!emailIsChanged) {
      setError("Enter a different email address.");
      return;
    }

    setError("");
    setStage("confirm");
  };

  const handleRequestEmailChange = async () => {
    if (!canContinue) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      await updateTeacherProfileWithEmail(schoolid, targetTeacher.id, {
        email: trimmedNewEmail,
        currentEmail,
        teacherName: targetTeacher.name,
        path
      });
      setRequestedChange({
        teacherName: targetTeacher.name,
        currentEmail,
        newEmail: trimmedNewEmail,
        isSelf: path === "self"
      });
      setStage("success");
    } catch (submitError) {
      console.error("Error requesting email change:", submitError);
      setError("The email could not be updated. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForAnother = () => {
    setStage("form");
    setPath("colleague");
    setTeacherSearchTerm("");
    setSelectedTeacher(null);
    setNewEmail("");
    setRequestedChange(null);
    setError("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: requestedChange?.teacherName || "",
        message: "Email updated."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderTeacherResults = () => {
    if (isLoading) {
      return (
        <div className="update-user-email-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading teachers...</span>
        </div>
      );
    }

    if (!teacherSearchTerm.trim()) {
      return <p className="update-user-email-muted">Start with the colleague's name or email.</p>;
    }

    if (filteredTeachers.length < 1) {
      return (
        <div className="update-user-email-empty">
          <strong>No matching teachers found.</strong>
          <span>Try another name or email address.</span>
        </div>
      );
    }

    return filteredTeachers.map((teacher) => {
      const selected = selectedTeacher?.id === teacher.id;

      return (
        <button
          key={teacher.id}
          type="button"
          className={`update-user-email-result${selected ? " selected" : ""}`}
          onClick={() => handleTeacherSelect(teacher)}
          aria-label={`Select ${teacher.name}`}
          aria-pressed={selected}
        >
          <span className="update-user-email-radio" aria-hidden="true" />
          <span className="update-user-email-result-copy">
            <strong>{teacher.name}</strong>
            <span>{teacher.email || "Email not available"}</span>
            {teacher.role && <span className="update-user-email-pill">{teacher.role}</span>}
          </span>
          {selected && <span className="update-user-email-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Update User Email</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="update-user-email-intro">Choose whose email should change.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="update-user-email-paths" role="group" aria-label="Choose email update path">
          <button
            type="button"
            className={`update-user-email-path${path === "colleague" ? " selected" : ""}`}
            onClick={() => handlePathChange("colleague")}
            aria-pressed={path === "colleague"}
          >
            <strong>A colleague's email</strong>
            <span>Find a teacher in this school.</span>
          </button>
          <button
            type="button"
            className={`update-user-email-path${path === "self" ? " selected" : ""}`}
            onClick={() => handlePathChange("self")}
            aria-pressed={path === "self"}
          >
            <strong>My email</strong>
            <span>Update your account email.</span>
          </button>
        </div>

        {path === "colleague" ? (
          <div className="update-user-email-section">
            <Form.Group controlId="teacherUpdateUserEmailSearch">
              <Form.Label>Which colleague?</Form.Label>
              <Form.Text as="div" className="update-user-email-field-label">
                Search colleague
              </Form.Text>
              <Form.Control
                type="search"
                value={teacherSearchTerm}
                onChange={(event) => {
                  setTeacherSearchTerm(event.target.value);
                  setSelectedTeacher(null);
                  setError("");
                }}
                aria-label="Search colleague"
              />
            </Form.Group>
            <div className="update-user-email-results" aria-label="Matching colleagues">
              {renderTeacherResults()}
            </div>
          </div>
        ) : (
          <div className="update-user-email-selected-panel">
            <strong>{selfTeacher?.name || "My email"}</strong>
            <span>{currentEmail || "Current email not available"}</span>
          </div>
        )}

        <Form.Group className="update-user-email-section" controlId="teacherUpdateUserEmailNewEmail">
          <Form.Label>New email address</Form.Label>
          <Form.Control
            type="email"
            value={newEmail}
            onChange={(event) => {
              setNewEmail(event.target.value);
              setError("");
            }}
            aria-label="New email address"
          />
          <Form.Text className="text-muted">
            The change will not be made until you confirm it.
          </Form.Text>
        </Form.Group>

        {targetTeacher && (
          <div className="update-user-email-note">
            <strong>Current email</strong>
            <span>{currentEmail || "Email not available"}</span>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <span className="update-user-email-footer-note">
          {targetTeacher ? `${targetTeacher.name} selected.` : "Choose who needs an email update."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleContinue} disabled={!canContinue}>
          Next
        </Button>
      </Modal.Footer>
    </>
  );

  const renderConfirm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Confirm Email Change</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="update-user-email-intro">Check the email change before confirming.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="warning" className="update-user-email-warning">
          <FaExclamationCircle aria-hidden="true" />
          <span>
            <strong>{targetTeacher?.name}'s account email will change.</strong>
            <span>{currentEmail} will change to {trimmedNewEmail}.</span>
          </span>
        </Alert>
        <div className="update-user-email-summary">
          <strong>Change details</strong>
          <span className="update-user-email-row">
            <span>Teacher</span>
            <span>{targetTeacher?.name}</span>
          </span>
          <span className="update-user-email-row">
            <span>Current email</span>
            <span>{currentEmail}</span>
          </span>
          <span className="update-user-email-row">
            <span>New email</span>
            <span>{trimmedNewEmail}</span>
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="update-user-email-footer-note">Confirmation required.</span>
        <Button variant="outline-secondary" onClick={() => setStage("form")}>
          Back
        </Button>
        <Button variant="success" onClick={handleRequestEmailChange} disabled={!canContinue}>
          {isSubmitting ? "Updating..." : "Update email"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Email Updated</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="update-user-email-intro">Account email updated immediately.</p>
        <Alert variant="success" className="update-user-email-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>{requestedChange?.teacherName}'s email has been updated.</strong>
            <span>{requestedChange?.currentEmail} changed to {requestedChange?.newEmail}</span>
          </span>
        </Alert>
        <div className="update-user-email-summary">
          <strong>What happens next</strong>
          <span>
            {requestedChange?.isSelf
              ? "Use the new email address to sign in."
              : `${requestedChange?.teacherName} should use the new email address to sign in.`}
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <span className="update-user-email-footer-note">
          {embedded ? "Done returns to SENSAI." : "Email updated."}
        </span>
        <Button variant="outline-secondary" onClick={resetForAnother}>
          Update another email
        </Button>
        {onSendPasswordReset && (
          <Button
            variant="outline-secondary"
            onClick={() => onSendPasswordReset({
              teacherName: requestedChange?.teacherName
            })}
          >
            Send password reset
          </Button>
        )}
        <Button variant="primary" onClick={handleDone}>
          Done
        </Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-update-user-email-modal"
    >
      {stage === "form" && renderForm()}
      {stage === "confirm" && renderConfirm()}
      {stage === "success" && renderSuccess()}
    </Modal>
  );
}

export default TeacherUpdateUserEmailModal;
